import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Avatar,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import EditIcon from "@material-ui/icons/Edit";
import GroupIcon from "@material-ui/icons/Group";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  backButton: { marginRight: theme.spacing(1) },
  table: { minWidth: 400 },
  emptyState: { textAlign: "center", padding: theme.spacing(4), color: theme.palette.text.secondary },
  avatar: { width: 40, height: 40 },
  nameCell: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
}));

const GroupEdit = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!connectionId) {
      history.replace("/group-management");
      return;
    }
    const fetchGroups = async () => {
      try {
        const { data } = await api.get(`/group-management/connections/${connectionId}/groups`, {
          params: { adminOnly: "true" },
        });
        setGroups(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [connectionId, history]);

  const handleBack = () => history.push("/group-management");
  const handleOpenGroup = (group) => {
    history.push(
      `/group-management/groups/${encodeURIComponent(group.id)}/edit?connectionId=${connectionId}`
    );
  };

  if (!connectionId) return null;

  return (
    <MainContainer>
      <MainHeader>
        <Button className={classes.backButton} onClick={handleBack} startIcon={<ArrowBackIcon />}>
          {i18n.t("groupManagement.back")}
        </Button>
        <Title>{i18n.t("groupManagement.editGroup")}</Title>
      </MainHeader>

      <Box className={classes.root}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              {i18n.t("groupManagement.editGroupAdminOnlyHint")}
            </Typography>
            <TableContainer component={Paper}>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" style={{ width: 56 }} />
                    <TableCell>{i18n.t("groupManagement.groupName")}</TableCell>
                    <TableCell align="right">{i18n.t("groupManagement.participantsCount")}</TableCell>
                    <TableCell align="center">{i18n.t("groupManagement.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Box className={classes.emptyState}>
                          <EditIcon style={{ fontSize: 48, marginBottom: 8 }} />
                          <Typography>{i18n.t("groupManagement.noAdminGroups")}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    groups.map((group) => (
                      <TableRow key={group.id} hover>
                        <TableCell padding="checkbox">
                          <Avatar
                            src={group.pictureUrl}
                            className={classes.avatar}
                            variant="circular"
                          >
                            <GroupIcon />
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Box className={classes.nameCell}>
                            <Typography variant="body2">{group.subject || group.id}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{group.participantsCount ?? "-"}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenGroup(group)}
                            title={i18n.t("groupManagement.editGroup")}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </MainContainer>
  );
};

export default GroupEdit;
